import * as React from "react";
import { useMemo } from "react";

import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Icon } from "../../components/ui/icon";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "../../components/ui/menu";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import type { GroupScope, SurfaceToken } from "../../components/ui/box-classes";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import { EditableTitleField } from "../inline-edit/inline-edit";
import {
  GRID_LAYER,
  WIDGET_CARD_ATTR,
  type SortableGridDragHandleProps,
} from "../sortable-grid";
import { WidgetSurfaceProvider } from "./contexts";
import { useWidgetRegistry } from "./registry-context";
import type { WidgetRegistry } from "./registry";
import type {
  WidgetConfig,
  WidgetInstance,
  WidgetPresentation,
  WidgetSurface,
} from "./types";
import { WidgetViewStateProvider } from "./view-state-context";
import { Widget } from "./widget";
import type { Omittable } from "../../lib/props";

/** The display title for an instance. A module that declares `dynamicTitle`
 *  OWNS title resolution: it gets the resolved config — including any stored
 *  `config.title` — and its return value is the title (the widget's own
 *  mechanics decide whether a stored title is a user rename to honor or a
 *  spelling it generated itself to re-derive). When the module declares no
 *  resolver (or returns undefined): `config.title`, else the static default. */
export function widgetTitle(instance: WidgetInstance, registry: WidgetRegistry): string {
  const cfgTitle = instance.config?.title;
  const stored = typeof cfgTitle === "string" && cfgTitle.length > 0 ? cfgTitle : undefined;
  const def = registry.get(instance.type);
  if (!def) return stored ?? instance.type;
  const resolved = def.dynamicTitle?.(
    registry.resolveConfig(def, instance.config, instance.schemaVersion ?? 1),
  );
  return resolved ?? stored ?? def.title;
}

/** One discrete width choice the host offers in the card's ellipsis menu. */
export type WidgetWidthOption = { value: number; label: string };

export type WidgetCardProps = Omittable<{
  instance: WidgetInstance;
  canManage: boolean;
  /** Layout authoring (drag-reorder + edge resize) split from `canManage` —
   *  managed surfaces hide manage chrome but keep arranging; mobile hosts pass
   *  false for view-only. Defaults to `canManage`. */
  canArrange?: boolean;
  /** Layout participation — the VIEW decides. `'grid'` (default): fixed cell,
   *  body owns scroll. `'stack'`: page flow — the card hugs content and the
   *  page scrolls (the issue view). */
  presentation?: WidgetPresentation;
  /** Which surface this card paints — the VIEW decides. See
   *  {@link WidgetSurface}. Resolution (the ONE place): `'canvas'` wins over
   *  everything (uniform substrate skin); else module `chromeless` forces
   *  `'canvas'`; else this value wins. `'none'` renders the body with NO
   *  shell at all. */
  surface?: WidgetSurface;
  /** The surface the BOARD behind the cards paints. `surface="canvas"`
   *  fills this surface exactly, so such a card masks the authoring guide
   *  dots by construction; `card` and `elevated` paint on top of it
   *  regardless. Defaults to `background` — a dashboard is a page. A
   *  dashboard mounted inside a card or a dialog names that surface
   *  instead. */
  canvas?: SurfaceToken;
  /** Stack-only disclosure: the header toggles the body. Grid ignores these. */
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Stack-only: render the body without title/disclosure chrome. */
  hideHeader?: boolean;
  dragHandleProps?: SortableGridDragHandleProps;
  isDragging?: boolean;
  /** True while THIS card is being edge-resized. The card itself renders no
   *  resize chrome (the grid owns the live span badge) — forwarded for custom
   *  card slots that want their own hint. */
  isResizing?: boolean;
  /** Live (preview-aware) span from the grid. While resizing these track the
   *  previewed cell so the body re-lays-out in real time; at rest they fall back
   *  to the committed instance span. */
  liveW?: number;
  liveH?: number;
  /** Consumer-resolved title override. `undefined` → kit fallback
   *  ({@link widgetTitle}: config.title, else module title). `null` → NO
   *  fallback: an empty config title renders no title. */
  title?: string | null;
  /** Leading header slot — the consumer supplies the node (e.g. a palette
   *  Badge). The kit renders placement only; it resolves no visuals. */
  icon?: React.ReactNode;
  /** Selection state — paints the card border `primary`. */
  selected?: boolean;
  /** Fired when the title area or body is clicked (e.g. select into an
   *  inspector). On a headerless tile the whole tile click fires it. */
  onSelect?: () => void;
  /** Trailing header cluster before the actions (badges, lock icons). On a
   *  headerless tile it joins the floating hover cluster. */
  headerAccessories?: React.ReactNode;
  /** Semantic edge accent: paints a thin color bar down the card's left edge
   *  (e.g. a per-owner tint on a shared canvas). Colors come from the shared
   *  `Color` scale; `undefined` renders no bar. Ignored on `surface="none"`. */
  accent?: Color;
  /** Maximize affordance — renders the widget fullscreen at its largest layout.
   *  `undefined` hides the button and menu item. */
  onMaximize?: () => void;
  /** Gear/settings affordance — opens the widget's settings dialog.
   *  `undefined` hides the gear. */
  onConfigure?: () => void;
  /** `undefined` hides the Delete menu item. */
  onDelete?: () => void;
  /** `undefined` hides the Duplicate menu item. */
  onDuplicate?: () => void;
  /** Swap this widget for a different module in place (opens the consumer's
   *  picker). `undefined` hides the Replace menu item. */
  onReplace?: () => void;
  /** Snap the tile back to its widget definition's default span.
   *  `undefined` hides the Reset-size menu item. */
  onResetSize?: () => void;
  /** Commit a new title to `config.title` (in-card editable title). */
  onRename?: (title: string) => void;
  /** Persist a config patch from in-widget interaction (e.g. a table column resize). */
  onConfigChange?: (patch: WidgetConfig) => void;
  /** Host-owned discrete width choices shown in the ellipsis menu (autoHeight
   *  widgets ride this instead of resize handles). */
  widthOptions?: readonly WidgetWidthOption[];
  /** Commit a host-owned width choice. */
  onWidthChange?: (width: number) => void;
  /** Natural content height (px) of an `autoHeight` widget — the grid host
   *  converts to whole rows (input-only px→rows, same role as pointer
   *  geometry) and feeds its derived live layout. Wired by `WidgetGrid`. */
  onContentHeightChange?: (px: number) => void;
  /** Hover-group scope for the card's hover-revealed chrome. Nested children
   *  inside a container card use `'tile'` so only the innermost hovered card
   *  lights its cluster (different scopes nest cleanly). Defaults to `'box'`. */
  hoverScope?: HoverScope;
  /** Hide the hover-revealed action chrome while any descendant widget card is
   *  hovered (container cards: hovering a child shows only the child's
   *  cluster). Static class — display beats the opacity reveal. */
  suppressActionsOnDescendantHover?: boolean;
}>;

/** The two hover scopes a widget card can own — a subset of Box's `GroupScope`
 *  so the card's `group` / `hoverReveal` pair is always a legal Box scope. A
 *  nested child card takes `'tile'` so only the innermost hovered card lights
 *  its cluster. */
type HoverScope = Extract<GroupScope, "box" | "tile">;

/** Hides an element while any descendant `[data-widgetcard]` (a nested child
 *  card, stamped via `WIDGET_CARD_ATTR`) is hovered, scoped to the card's own
 *  hover group. Tailwind only emits classes present verbatim in source, so
 *  both spellings stay literal. */
const SUPPRESS_ON_DESCENDANT_HOVER: Record<HoverScope, string> = {
  box: "group-has-[[data-widgetcard]:hover]/box:hidden",
  tile: "group-has-[[data-widgetcard]:hover]/tile:hidden",
};

/** Manage-gated hover cue, keyed by the surface the card paints so the cue is
 *  that surface's own louder line rather than the page's: `elevated` rests on
 *  a quieter hairline than `card`, and a cue has to clear the line it
 *  replaces. Literal strings for the Tailwind scanner. */
const HOVER_BORDER: Record<"card" | "elevated", string> = {
  card: "hover:border-(--card-border-strong) transition-colors",
  elevated: "hover:border-(--elevated-border-strong) transition-colors",
};

/** A card pinned to the canvas keeps the card's SHAPE and drops every bit of
 *  its chrome: no drop, no inner highlight, and a hairline that is reserved
 *  rather than absent so the geometry never shifts when a cue colors it in.
 *  The fill still comes from `Box surface` — only the chrome is dropped. */
const CANVAS_CHROME = "border-transparent shadow-none before:hidden";

/**
 * THE chrome around a widget body — the only widget chrome in the system; custom
 * `renderCard` slots compose this component with the slot props (`icon`,
 * `title`, `selected`, `onSelect`, `headerAccessories`), never a
 * parallel Card tree.
 *
 * ONE owner per appearance axis:
 * - module `chrome` ('card' | 'headerless' | 'container') — STRUCTURE (headed
 *   / headerless / container-pill) plus the body flags (`bleed`, `flushBody`,
 *   `bodyDraggable`, `autoHeight`) — what the widget IS, never per view.
 * - module `chromeless` — "renders as content, not a card".
 * - host `presentation` ('grid' | 'stack') — layout participation.
 * - host `surface` ('card' | 'elevated' | 'canvas' | 'none') — what it paints.
 *
 * Resolution (here, nowhere else): `canvas` wins over everything and pins
 * every card to the substrate; else module `chromeless` forces `canvas`; else
 * the host's value wins. `accent` stays orthogonal on any surface but `none`.
 *
 * Elevation is named surfaces, never ad-hoc shadows, and the prop value IS
 * the surface name — there is no mapping to learn. `elevated` is a card
 * floating over the page: a longer, more diffuse drop and a quieter hairline
 * than `card`. A dragging card borrows the `popover` shadow on top of either,
 * the one moment it is truly above the page. `canvas` paints the board's own
 * surface — the `canvas` prop, which the host names (default `background`,
 * the page). Whatever the card resolves to paint it publishes through
 * `WidgetSurfaceProvider`, so a body's own opaque parts (a DataTable's sticky
 * header) blend with the card via `useWidgetSurface()`.
 */
export function WidgetCard({
  instance,
  canManage,
  canArrange,
  presentation = "grid",
  surface = "card",
  canvas = "background",
  collapsible = false,
  collapsed = false,
  onCollapsedChange,
  hideHeader = false,
  dragHandleProps,
  isDragging,
  liveW,
  liveH,
  title,
  icon,
  selected,
  onSelect,
  headerAccessories,
  accent,
  onMaximize,
  onConfigure,
  onDelete,
  onDuplicate,
  onReplace,
  onResetSize,
  onRename,
  onConfigChange,
  widthOptions,
  onWidthChange,
  onContentHeightChange,
  hoverScope = "box",
  suppressActionsOnDescendantHover,
}: WidgetCardProps) {
  const registry = useWidgetRegistry();
  const isTouch = useIsTouchDevice();
  const def = registry.get(instance.type);
  const arrange = canArrange ?? canManage;
  const chrome = def?.chrome ?? "card";
  const stack = presentation === "stack";
  const headerMode = chrome === "card" || stack ? "default" : "none";
  const containerChrome = !stack && chrome === "container";
  const bleed = def?.bleed ?? false;
  const flushBody = def?.flushBody ?? false;
  const bodyDraggable = def?.bodyDraggable ?? false;
  const autoHeight = (def?.autoHeight ?? false) && !stack;
  const HeaderActions = def?.headerActions;
  const TitleAdornment = def?.titleAdornment;
  // Header-slot renderers get the same resolved config as the body (the
  // harness resolves again for the body — same memoized inputs, cheap).
  const resolvedConfig = useMemo(
    () => registry.resolveConfig(def, instance.config, instance.schemaVersion ?? 1),
    [registry, def, instance.config, instance.schemaVersion],
  );

  // ONE resolution point for what the card paints (see the component doc).
  // A chromeless module renders as content — no fill at all — which removes
  // even more than `canvas`, so a host that already pinned every card to the
  // canvas keeps that and the module flag does not apply.
  // `none` never reaches a shell — it returns the bare body below — so the
  // paintable set excludes it and every branch can name a real surface.
  const chromeless = surface !== "canvas" && (def?.chromeless ?? false);
  const paints: Exclude<WidgetSurface, "none"> =
    chromeless || surface === "none" ? "canvas" : surface;
  // A painted card names a real surface and wears the full card chrome. A
  // canvas card wears the shape only. Every appearance branch below asks this
  // once. `bodySurface` is what the body actually sits on — the card's own
  // fill, or the board's for a canvas/none card — published to the body.
  const painted = paints !== "canvas";
  const bodySurface: SurfaceToken = painted ? paints : canvas;

  const resolvedTitle = title === undefined ? widgetTitle(instance, registry) : (title ?? "");
  const w = liveW ?? instance.w;
  const h = liveH ?? instance.h;
  // A select click is consumed here: nested child cards render INSIDE a
  // container tile's own select surface, so a bubbling click would re-select
  // the ancestor and clobber the child selection — innermost card wins.
  const handleSelect = onSelect
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect();
      }
    : undefined;
  // A tiny card (≤2 cells) hides Maximize — the only thing the chrome needs to
  // know about size.
  const compact = w * h <= 2;
  // Narrow cards (≤3 columns) collapse Maximize/Settings into the menu so
  // widget-contributed header controls can breathe.
  const small = !stack && w <= 3;

  // The drag surface (header row, whole tile, or — for bodyDraggable — the
  // whole card). We wrap `onKeyDown` so a keyboard drag only starts when the
  // handle itself is the focused target — otherwise Space/Enter typed into the
  // title editor would bubble up and grab the card.
  const guardedDragProps: SortableGridDragHandleProps | undefined =
    arrange && dragHandleProps
      ? {
          ...dragHandleProps,
          onKeyDown: (event) => {
            if (event.target === event.currentTarget) {
              dragHandleProps.onKeyDown?.(event);
            }
          },
        }
      : undefined;
  const dragCursor = guardedDragProps ? (isDragging ? "grabbing" : "grab") : undefined;
  // Stack cards drag only from the disclosure gutter; bodyDraggable grid cards
  // drag from the whole card; everything else drags from its header/tile.
  const cardDragProps = !stack && bodyDraggable ? guardedDragProps : undefined;
  const headerDragProps = stack || bodyDraggable ? undefined : guardedDragProps;
  const stackGutterDragProps = stack ? guardedDragProps : undefined;

  // AutoHeight measurement: the inner content wrapper hugs its natural height
  // while the Card fills the whole-row-quantized cell — a ResizeObserver on
  // the wrapper reports px UP to the grid host, which converts to whole rows.
  // Input-only: NO px concept ever reaches the rendered layout from here.
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const reportRef = React.useRef(onContentHeightChange);
  reportRef.current = onContentHeightChange;
  const measure = autoHeight && onContentHeightChange != null;
  React.useEffect(() => {
    if (!measure) return;
    const node = contentRef.current;
    if (!node) return;
    const report = () => reportRef.current?.(node.offsetHeight);
    const observer = new ResizeObserver(report);
    observer.observe(node);
    report();
    return () => observer.disconnect();
  }, [measure]);

  const stopHeaderPropagation = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  // Hover-revealed chrome, spread onto every Box that carries some. Box owns
  // the reveal pair (`group` on the ancestor, `hoverReveal` on the chrome) so
  // the two class names can never drift; `revealOnTouch` keeps the affordance
  // visible where CSS hover never fires.
  const reveal = { hoverReveal: hoverScope, revealOnTouch: true } as const;

  // Affordance gates. Maximize is NOT manage-gated (viewers open the detail
  // dialog's data view); Settings and the manage menu items are; width options
  // are host-gated (the host only passes them where width changes are allowed).
  const showMaximize = !!onMaximize;
  const showConfigure = canManage && !!onConfigure;
  const widthMenu = (widthOptions?.length ?? 0) > 0;
  const manageMenuItems = canManage && !!(onDuplicate || onReplace || onResetSize || onDelete);
  // Maximize/Settings appear as MENU items only when their header button is
  // collapsed away — every separator below must gate on these collapsed terms
  // (not the raw affordances) or it renders orphaned above/below nothing.
  const menuMaximize = showMaximize && (small || compact);
  const menuConfigure = showConfigure && small;
  const menuAvailable = widthMenu || manageMenuItems || menuMaximize || menuConfigure;
  const clusterAvailable = showMaximize || showConfigure || menuAvailable;
  // Header action ORDER: Maximize → domain headerActions → Settings → menu;
  // Maximize/Settings collapse into the menu at small widths.
  const actionCluster = clusterAvailable ? (
    <>
      {showMaximize && !compact && !small ? (
        <Button variant="ghost" size="icon-xs" onClick={onMaximize} aria-label="Maximize">
          <Icon name="maximize-2" size="sm" color="muted" />
        </Button>
      ) : null}
      {showConfigure && !small ? (
        <Button variant="ghost" size="icon-xs" onClick={onConfigure} aria-label="Settings">
          <Icon name="settings" size="sm" color="muted" />
        </Button>
      ) : null}
      {menuAvailable ? (
        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon-xs" aria-label="Widget menu">
                <Icon name="more-horizontal" size="sm" color="muted" />
              </Button>
            }
          />
          <MenuPopup align="end" sideOffset={4}>
            {widthOptions?.map((option) => (
              <MenuItem key={option.value} onClick={() => onWidthChange?.(option.value)}>
                <Icon name={w === option.value ? "check" : "columns-2"} size="sm" />
                {option.label}
              </MenuItem>
            ))}
            {widthMenu && (manageMenuItems || menuMaximize || menuConfigure) ? (
              <MenuSeparator />
            ) : null}
            {menuMaximize ? (
              <MenuItem onClick={onMaximize}>
                <Icon name="maximize-2" size="sm" />
                Maximize
              </MenuItem>
            ) : null}
            {menuConfigure ? (
              <MenuItem onClick={onConfigure}>
                <Icon name="settings" size="sm" />
                Settings
              </MenuItem>
            ) : null}
            {canManage && onDuplicate ? (
              <MenuItem onClick={onDuplicate}>
                <Icon name="copy" size="sm" />
                Duplicate
              </MenuItem>
            ) : null}
            {canManage && onReplace ? (
              <MenuItem onClick={onReplace}>
                <Icon name="replace" size="sm" />
                Replace
              </MenuItem>
            ) : null}
            {canManage && onResetSize ? (
              <MenuItem onClick={onResetSize}>
                <Icon name="minimize-2" size="sm" />
                Reset size
              </MenuItem>
            ) : null}
            {canManage &&
            onDelete &&
            (widthMenu || menuMaximize || menuConfigure || onDuplicate || onReplace || onResetSize) ? (
              <MenuSeparator />
            ) : null}
            {canManage && onDelete ? (
              <MenuItem variant="destructive" onClick={onDelete}>
                <Icon name="trash-2" size="sm" />
                Delete
              </MenuItem>
            ) : null}
          </MenuPopup>
        </Menu>
      ) : null}
    </>
  ) : null;

  // Widget-contributed header controls: hover-revealed with the manage cluster
  // but NOT manage-gated (viewers can filter).
  const domainActions = HeaderActions ? (
    <Box
      {...reveal}
      onClick={stopHeaderPropagation}
      onKeyDown={stopHeaderPropagation}
    >
      <HeaderActions w={w} h={h} config={resolvedConfig} onConfigChange={onConfigChange} />
    </Box>
  ) : null;

  const body = (
    <Widget
      module={instance.type}
      config={instance.config ?? undefined}
      schemaVersion={instance.schemaVersion}
      w={w}
      h={h}
      onConfigChange={onConfigChange}
    />
  );

  // `none`: NO shell — no Card, no header, no padding/scroll wrapper. The body
  // owns its entire presentation (print/read-only hosts). The view
  // state provider adds no DOM, so this stays pixel-identical to a raw body.
  if (surface === "none") {
    return (
      <WidgetViewStateProvider>
        <WidgetSurfaceProvider value={bodySurface}>{body}</WidgetSurfaceProvider>
      </WidgetViewStateProvider>
    );
  }

  // A manageable widget always shows the header (it's the drag handle and
  // title editor); an unmanaged one collapses it when there's nothing to show.
  const showHeader =
    headerMode === "default" &&
    !(stack && hideHeader) &&
    (canManage ||
      clusterAvailable ||
      resolvedTitle.length > 0 ||
      headerAccessories != null ||
      HeaderActions != null);

  const suppressClass = suppressActionsOnDescendantHover
    ? SUPPRESS_ON_DESCENDANT_HOVER[hoverScope]
    : undefined;

  const stackCollapseLabel = `${collapsed ? "Expand" : "Collapse"} ${resolvedTitle}`;

  const header = showHeader ? (
    <Box
      direction="row"
      align="center"
      gap={stack ? "2xs" : "xs"}
      paddingX={stack && !painted ? undefined : "md"}
      paddingTop={stack ? "xs" : "sm"}
      paddingBottom={stack ? "xs" : undefined}
      minHeight={stack ? 30 : 34}
      cursor={
        stack && collapsible
          ? "pointer"
          : headerDragProps
            ? isDragging
              ? "grabbing"
              : "grab"
            : undefined
      }
      hoverBackground={stack && collapsible}
      rounded={stack && collapsible ? "md" : undefined}
      onClick={stack && collapsible ? () => onCollapsedChange?.(!collapsed) : undefined}
      aria-label={stack && collapsible ? stackCollapseLabel : undefined}
      aria-expanded={stack && collapsible ? !collapsed : undefined}
      {...headerDragProps}
    >
      {stack && collapsible ? (
        // The disclosure gutter doubles as the stack card's only drag surface:
        // the chevron swaps to a grip on hover when the card is arrangeable.
        <Box
          direction="row"
          align="center"
          justify="center"
          shrink={false}
          paddingRight="2xs"
          position="relative"
          group={stackGutterDragProps ? "gutter" : undefined}
          cursor={stackGutterDragProps ? (isDragging ? "grabbing" : "grab") : undefined}
          {...stackGutterDragProps}
        >
          <Box pointerEvents="none"
            direction="row"
            align="center"
            justify="center"
            className={cn(stackGutterDragProps && !isTouch &&
                "transition-opacity group-hover/gutter:opacity-0",
            )}
          >
            <Icon name={collapsed ? "chevron-right" : "chevron-down"} size="sm" color="muted" />
          </Box>
          {stackGutterDragProps ? (
            <Box pointerEvents="none"
              direction="row"
              fill="absolute"
              align="center"
              justify="center"
              className={cn(!isTouch && "opacity-0 transition-opacity group-hover/gutter:opacity-100",
              )}
            >
              <Icon name="grip-vertical" size="sm" color="muted" />
            </Box>
          ) : null}
        </Box>
      ) : null}
      {icon}
      {/* The title HUGS its text so the adornment ("· 12") sits BESIDE it —
          the flexible spacer after the adornment absorbs the leftover width
          and doubles as the header's click-to-select surface. */}
      <Box minWidth={0} shrink direction="row" align="center" onClick={handleSelect}>
        <EditableTitleField
          flushLeft={!icon}
          size={stack ? "base" : "sm"}
          weight="semibold"
          value={resolvedTitle}
          editable={Boolean(canManage && onRename)}
          onCommit={(next) => onRename?.(next)}
          aria-label="Widget name"
        />
      </Box>
      {TitleAdornment ? (
        <Box shrink={false}>
          <TitleAdornment w={w} h={h} config={resolvedConfig} onConfigChange={onConfigChange} />
        </Box>
      ) : null}
      <Box flex={1} className="self-stretch" onClick={handleSelect} />
      {headerAccessories}
      {domainActions}
      {actionCluster ? (
        <Box
          direction="row"
          align="center"
          gap="2xs"
          {...reveal}
          onClick={stopHeaderPropagation}
          onKeyDown={stopHeaderPropagation}
        >
          {actionCluster}
        </Box>
      ) : null}
    </Box>
  ) : null;

  const accentBar = accent ? (
    <Box pointerEvents="none" position="absolute"
      data-color={accent}
      className="inset-y-0 left-0 w-1 bg-c"
    />
  ) : null;

  // ------ STACK PRESENTATION: a page-flow card that hugs content ------
  if (stack) {
    return (
      <WidgetViewStateProvider>
        <WidgetSurfaceProvider value={bodySurface}>
          <Card
            radius="lg"
            group={hoverScope}
            // Same resolution as the grid card below: the named surface comes
            // from Box, and a canvas card is a plain section — shape only, and
            // no fill at all, because a page-flow stack has no guide dots to
            // mask.
            surface={painted ? paints : undefined}
            className={cn(
              "p-0 overflow-visible transition-opacity",
              !painted && cn(CANVAS_CHROME, "bg-transparent"),
              selected && "border-primary",
              isDragging && "opacity-70",
            )}
          >
            {accentBar}
            {header}
            {collapsible && collapsed ? null : (
              <Box
                onClick={handleSelect}
                paddingX={painted ? "md" : undefined}
                paddingBottom={showHeader || painted ? "md" : undefined}
                paddingTop={showHeader ? "xs" : undefined}
              >
                {body}
              </Box>
            )}
          </Card>
        </WidgetSurfaceProvider>
      </WidgetViewStateProvider>
    );
  }

  // ------ GRID PRESENTATION ------
  const bodyPaddings = {
    paddingX: bleed || flushBody ? undefined : ("md" as const),
    paddingBottom: flushBody ? undefined : ("md" as const),
    paddingTop: flushBody ? undefined : showHeader ? ("xs" as const) : ("md" as const),
  };

  // The floating hover cluster a headerless tile pins to its corner — an
  // overlay-tier chip that reads above any surface.
  const tileCluster =
    (canManage || clusterAvailable) && (actionCluster || headerAccessories != null) ? (
      <Box
        position="absolute"
        top={6}
        right={6}
        {...reveal}
        className={suppressClass}
      >
        <Box direction="row" align="center" gap="2xs" padding="2xs"
          surface="popover"
          rounded="md"
          border
        >
          {headerAccessories}
          {domainActions}
          {actionCluster}
        </Box>
      </Box>
    ) : null;

  const inner =
    headerMode === "none" ? (
      containerChrome ? (
        // Container chrome: the tile is NOT a whole-surface drag handle (the
        // nested grid owns its own background gestures) and the hover chrome
        // is the labeled pill rendered as a sibling of the Card below — the
        // card's overflow clip would swallow a border-straddling pill.
        // The body is FLUSH: no scroll wrapper (containers never legitimately
        // scroll — height derives from children — and `resolveFitTarget` must
        // not find a scroll viewport inside one) and no padding, so the
        // nested grid sits directly on the card and nested cells land ON page
        // cells (the reserved 1px border is the only residual phase error).
        <Box flex={1} fill="width" direction="column" onClick={handleSelect}>
          {body}
        </Box>
      ) : (
        <Box position="relative" flex={1} fill="width"
          direction="column"
          onClick={handleSelect}
          cursor={dragCursor}
          {...guardedDragProps}
        >
          {autoHeight ? (
            <Box ref={contentRef} paddingX="md" paddingY="md">
              {body}
            </Box>
          ) : flushBody ? (
            // flushBody: the widget owns its scrollport — no scroll wrapper,
            // no padding; the body fills the tile edge to edge.
            <Box flex={1} fill="width" direction="column" className="h-full">{body}</Box>
          ) : (
            <ScrollArea fill>
              {/* Flex column, not a block: every widget body root claims its
                  height with `fill` (flex-1 min-h-0). In a block parent that
                  is inert, the root collapses to content height, and a
                  self-measuring child — an XYChart's ParentSize, a map canvas
                  — resolves `height: 100%` against `auto` and renders at 0. */}
              <Box fill="size" direction="column" paddingX="md" paddingY="md">
                {body}
              </Box>
            </ScrollArea>
          )}
          {tileCluster}
        </Box>
      )
    ) : autoHeight ? (
      // AutoHeight: the measured wrapper hugs header + body natural height
      // inside the cell-filling Card. No scroll wrapper — content never
      // scrolls; the grid grows the row span instead.
      <Box ref={contentRef}>
        {header}
        <Box onClick={handleSelect} {...bodyPaddings}>
          {body}
        </Box>
      </Box>
    ) : (
      <>
        {header}
        <Box position="relative" minHeight={0} flex={1} direction="column" onClick={handleSelect}>
          {flushBody ? (
            // flushBody: the widget owns its scrollport — no scroll wrapper,
            // no padding; the body fills the card edge to edge below the header.
            <Box fill="size" direction="column">{body}</Box>
          ) : (
            <ScrollArea fill>
              {/* Flex column — see the headerless branch above. */}
              <Box fill="size" direction="column" {...bodyPaddings}>
                {body}
              </Box>
            </ScrollArea>
          )}
        </Box>
      </>
    );

  // Container pill: labeled hover chrome straddling the container's top-left
  // border. A sibling of the Card inside the hover-group wrapper below —
  // inside the Card the overflow clip would swallow the protruding half, and
  // the `group-hover` reveal needs the group class on an ANCESTOR, which a
  // bare sibling never satisfies. Always visible on touch devices (CSS hover
  // never fires there). `GRID_LAYER.pill` keeps the protruding half above
  // later-in-DOM sibling tiles (grid items are zIndex:auto at rest, so the
  // pill's z participates at grid level).
  const pillLabel = def ? (
    <Box direction="row" align="center" gap="2xs" paddingY="2xs">
      <Icon name={def.icon} size="sm" color="muted" />
      <Text size="xs" weight="medium" variant="muted">
        {def.title}
      </Text>
    </Box>
  ) : null;
  const containerPill =
    containerChrome && (canManage || arrange) && def ? (
      <Box
        position="absolute"
        top={-12}
        left={12}
        zIndex={GRID_LAYER.pill}
        {...reveal}
        className={suppressClass}
      >
        <Box direction="row" align="center" gap="2xs"
          surface="popover"
          rounded="md"
          border
          paddingX="xs"
        >
          {guardedDragProps ? (
            <Box
              cursor={isDragging ? "grabbing" : "grab"}
              {...guardedDragProps}
            >
              {pillLabel}
            </Box>
          ) : (
            pillLabel
          )}
          {headerAccessories}
          {domainActions}
          {actionCluster}
        </Box>
      </Box>
    ) : null;

  const cardShapeClass = cn(
    "size-full p-0 overflow-hidden",
    // Container chrome: the card's top+bottom 1px borders live INSIDE the
    // grid cell, but the flush nested grid runs fixed-pitch rows that need
    // the cell's FULL height — without compensation the last nested row
    // overflows the card's clip line by exactly 2px, swallowing children's
    // bottom borders and most of their s-resize handles. The negative margin
    // lets the flex-filled card grow those 2px back (into the ≥8px page gap
    // below — invisible), landing the last row's bottom edge exactly on the
    // clip line.
    containerChrome && "-mb-0.5",
    cardDragProps && (isDragging ? "cursor-grabbing" : "cursor-grab"),
  );

  // The hairline cue, merged LAST below. Everything before it writes a border
  // color of its own — the surface's on a card that names one, transparent on
  // one pinned to the canvas — and tw-merge keeps whichever class comes later,
  // so a cue merged earlier is dropped and selection paints nothing.
  //
  // Manageable cards hint their bounds on hover; `selected` outranks all hover
  // cues.
  const borderCueClass = selected
    ? "border-primary"
    : canManage
      ? painted
        ? HOVER_BORDER[paints]
        : "hover:border-border transition-colors"
      : undefined;
  // Container cards move the hover group to the wrapper below (the pill is a
  // Card sibling and `group-hover` needs an ancestor group) and skip the
  // widgetcard stamp: the suppression rule keys on descendant
  // `[data-widgetcard]:hover`, and with the group on the wrapper the
  // container's OWN card would match — hovering the container itself would
  // hide its pill. Child cards (never containers; depth is 1) keep the stamp
  // so the suppression matches exactly them.
  const cardStamp = containerChrome ? undefined : { [WIDGET_CARD_ATTR]: "" };

  const card = (
    <Card
      radius="lg"
      // Container cards move the hover group to the wrapper below.
      group={containerChrome ? undefined : hoverScope}
      // ONE prop decides the whole appearance: the card names a surface and
      // Box paints that surface's fill, hairline and drop. `elevated` is a
      // card floating over the page — longer drop, quieter hairline. A canvas
      // card names the BOARD's surface instead, so its fill matches the
      // substrate exactly and it masks the authoring guide dots by
      // construction. Dragging borrows the `popover` shadow, the one moment
      // the card really is above the page, so only the shadow changes and
      // never the color.
      surface={painted ? paints : canvas}
      surfaceShadow={painted && isDragging ? "popover" : undefined}
      className={cn(
        cardShapeClass,
        painted
          ? "transition-shadow"
          : // Shape only. `chromeless` goes one further and drops the fill
            // too: the widget renders as content, not as a card.
            cn(CANVAS_CHROME, chromeless && "bg-transparent"),
        borderCueClass,
      )}
      {...cardStamp}
      {...cardDragProps}
    >
      {accentBar}
      {inner}
    </Card>
  );

  const dressed = !containerPill ? (
    card
  ) : (
    // The pill's hover-group ancestor: fills the grid item, so the pill
    // anchors to the item's top/left.
    <Box fill="size" position="relative" group={hoverScope}>
      {card}
      {containerPill}
    </Box>
  );

  return (
    <WidgetViewStateProvider>
      <WidgetSurfaceProvider value={bodySurface}>{dressed}</WidgetSurfaceProvider>
    </WidgetViewStateProvider>
  );
}
